// Added By Farhan Khan on 25-Feb-2026 (Logic)
import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import TextField from "@mui/material/TextField";
import {
  Button,
  Col,
  Container,
  Row,
  Form,
  Breadcrumb,
  Stack,
} from "react-bootstrap";
import {
  DatatableWrapper,
  PaginationOptions,
} from "react-bs-datatable";
import { Link, useNavigate } from "react-router-dom";
import solisPowerApi from "../../api/solisPowerApi";
import ".././../assets/css/ListComponentsTable.css";
import { ROUTES } from "../../constants/webRouting";
import { formatDate, formatDateForViewIST } from "../../utils/helper";
import InfoPill from "../InfoPill";
import { DateRangePicker } from "rsuite";
import * as constants from "../../constants/CONSTANT";

const ProjectList = () => {
  const navigate = useNavigate();
  const [body, setBody] = useState();
  const [filterData, setFilterData] = useState({});
  const [dateRange, setDateRange] = React.useState([]);
  const [statusArray, setStatusArray] = useState(constants.PROJECT_STATUS_VALUES);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const filter = { ...filterData };
    if (Array.isArray(dateRange) && dateRange.length > 0) {
      filter.date_range = {
        filed: "createddate",
        createddate: {
          from: formatDate(dateRange?.at(0) || null),
          to: formatDate(dateRange?.at(1) || null),
        },
      };
    }
    fetchProjects(filter);
  }, [dateRange, filterData]);

  async function fetchProjects(filters) {
    const params = { ...filters };
    if (params.date_range?.createddate) {
      params.fromDate = params.date_range.createddate.from;
      params.toDate = params.date_range.createddate.to;
      delete params.date_range;
    }
    const result = await solisPowerApi.fetchProjects(params);
    if (result && Array.isArray(result)) {
      setBody(result);
      const mappedRows = result.map((item, index) => ({
        id: item.id || index,
        lead_no: item.lead_no || "",
        lead_name: item.lead_name || "",
        project_name: item.project_name || "",
        project_status: item.project_status || "",
        lead_id: item.lead_id || null,
        createddate: item.createddate || "",
        createdbyname: item.createdbyname || "",
      }));
      setRows(mappedRows);
      setFilteredRows(mappedRows);
    } else {
      setBody([]);
      setRows([]);
      setFilteredRows([]);
    }
  }

  const header = [
    {
      title: "Lead No",
      prop: "lead_no",
      cell: (row) => (
        <Link
          to={`${ROUTES.LEAD.LIST}/${row.lead_id}`}
          state={{ id: row.lead_id }}
          style={{ color: "#1a73e8", textDecoration: "none", fontWeight: 600 }}
        >
          {row.lead_no}
        </Link>
      ),
      isFilterable: true,
      isSortable: true,
    },
    {
      title: "Lead Name",
      prop: "lead_name",
      isFilterable: true,
      isSortable: true,
    },
    {
      title: "Project Name",
      prop: "project_name",
      isFilterable: true,
      isSortable: true,
    },

    {
      title: "Project Status",
      prop: "project_status",
      isFilterable: true,
      isSortable: true,
      alignment: { horizontal: "center" },
      cell: (row) => {
        const statusObj = constants.PROJECT_STATUS_VALUES.find(
          (v) => v.label === row.project_status
        );
        const css = statusObj?.css || {};
        return (
          <span
            className="badge rounded-pill w-100"
            style={css}
          >
            {row.project_status}
          </span>
        );
      },
    },
    {
      title: "Created Date",
      prop: "createddate",
      isFilterable: true,
      cell: (row) =>
        row?.createddate ? formatDateForViewIST(row.createddate) : "",
      isSortable: true,
    },
    {
      title: "Created By",
      prop: "createdbyname",
      isFilterable: true,
      isSortable: true,
    },
  ];

  const labels = { beforeSelect: " " };

  const handleChange = (e) => {
    setFilterData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleClear = () => {
    setFilterData({});
    setDateRange([]);
    setSearchText("");
    fetchProjects({});
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const filtered = rows.filter((row) =>
      Object.values(row).some(
        (field) =>
          field && field.toString().toLowerCase().includes(value.toLowerCase())
      )
    );
    setFilteredRows(filtered);
  };

  const columns = [
    {
      field: "project_name",
      headerName: "Project Name",
      flex: 1,
      sortable: true,
    },
    {
      field: "project_status",
      headerName: "Project Status",
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const statusObj = constants.PROJECT_STATUS_VALUES.find(
          (v) => v.label === params.row.project_status
        );
        const css = statusObj?.css || {};
        return (
          <span className="badge rounded-pill w-100" style={css}>
            {params.row.project_status}
          </span>
        );
      },
    },
    {
      field: "lead_no",
      headerName: "Lead No",
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Link
            to={`${ROUTES.LEAD.LIST}/${row.lead_id}`}
            state={{ id: row.lead_id }}
            style={{
              color: "#1a73e8",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            {row.lead_no}
          </Link>
        );
      },
    },

    {
      field: "lead_name",
      headerName: "Lead Name",
      flex: 1,
      sortable: true,
    },
    {
      field: "createddate",
      headerName: "Created Date",
      flex: 1,
      renderCell: (params) =>
        params.row?.createddate
          ? formatDateForViewIST(params.row.createddate)
          : "",
    },
    {
      field: "createdbyname",
      headerName: "Added By",
      flex: 1,
      sortable: true,
    },
  ];

  return (
    <Container fluid>
      <DatatableWrapper
        body={body && Array.isArray(body) ? body : []}
        headers={header}
        paginationOptionsProps={{
          initialState: {
            rowsPerPage: 50,
            options: [5, 10, 15, 20, 30, 50],
          },
        }}
        sortProps={{ initialState: { prop: "createddate", order: "desc" } }}
      >
        <Row className="">
          <Col lg={12} className="">
            <Breadcrumb>
              <Breadcrumb.Item onClick={() => navigate(ROUTES.HOME)}>
                Home
              </Breadcrumb.Item>
              <Breadcrumb.Item onClick={() => navigate(ROUTES.PROJECT.LIST)}>
                Projects
              </Breadcrumb.Item>
            </Breadcrumb>
          </Col>
          <Col lg={3}>
            <Form.Group className="mt-2" controlId="formProjectStatus">
              <Form.Label>Project Status</Form.Label>
              <Form.Select
                name="project_status"
                value={filterData?.project_status || ""}
                onChange={handleChange}
              >
                <option value="">--Select Status--</option>
                {statusArray.map((item, index) => (
                  <option value={item.label} key={index}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        <Row className="align-items-end">
          <Col>
            <div className="leads-header">
              <div className="leads-actions">
                <TextField
                  size="small"
                  placeholder="Search Projects..."
                  className="custom-search-field"
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            </div>
          </Col>
          <Col md={1}>
            <PaginationOptions labels={labels} />
          </Col>
          <Col>
            <DateRangePicker
              value={dateRange}
              onChange={(e) => setDateRange(e || [])}
              character=" – "
            />
          </Col>
          <Col className="py-1">
            <div className="d-flex py-1 px-2 ">
              <InfoPill
                left="Total"
                right={filteredRows?.length ?? 0}
                rightClasses="bg-custom text-white border-white fw-bold"
                leftClasses="bg-white  fw-bold "
              />
            </div>
          </Col>
          <Col
            xs={12}
            sm={6}
            lg={4}
            className="d-flex flex-col justify-content-end align-items-end"
          >
            <Stack direction="horizontal" gap={2}>
              <Button
                variant="primary"
                onClick={() => navigate(ROUTES.PROJECT.DASHBOARD)}
              >
                Project Dashboard
              </Button>
              <Button variant="secondary" onClick={handleClear}>
                <i className="fa fa-refresh" aria-hidden="true"></i>
              </Button>
            </Stack>
          </Col>
        </Row>
      </DatatableWrapper>
      <div className="leads-page">
        <div
          style={{
            height: 400,
            width: "100%",
            marginTop: "20px",
            boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
          }}
        >
          <DataGrid
            rows={Array.isArray(filteredRows) ? filteredRows : []}
            columns={columns}
            pageSize={25}
            rowsPerPageOptions={[25]}
            getRowHeight={() => "auto"}
            disableRowSelectionOnClick
            className="custom-datagrid"
          />
        </div>
      </div>
    </Container>
  );
};

export default ProjectList;
